import {
  useResponseRedirect,
  useRedirectToPage,
  useRoleForBorrower,
} from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  fromHomePlacement,
  toUpdateHomePlacementReq,
  type HomePlacementFormData,
} from "src/api/adapters";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import {
  lessThanMaxLength,
  notEmpty,
  test,
  validate,
  validName,
  validStreetAddress,
  validZipcode,
  type ValidationMapperFunc,
} from "src/utils";
import {
  FormTable,
  FormTableBody,
  HomeFields,
  MapsLocation02Icon,
} from "@porchpass/ui";
import { useMemo } from "react";
import type { DtoHome } from "@porchpass/api-client";
import { addressErrorsAfterSubmit } from "src/forms/adapters/address";
import { googlePlacesFieldProps } from "src/constants/googlePlaces";
import {
  fromHomePlacementFormData,
  toHomePlacementFormData,
} from "src/forms/adapters/home";

const validationMapper: ValidationMapperFunc<HomePlacementFormData> = (_) => ({
  street1: [
    test(validStreetAddress, "Must be a valid street address"),
    test(lessThanMaxLength(120), "Address must not exceed 120 characters"),
  ],
  city: [test(lessThanMaxLength(70), "City must not exceed 70 characters")],
  county: [test(validName, "Must be a valid county name")],
  state: [test(notEmpty, "State must not be empty")],
  zip_code: [
    test(notEmpty, "Zip Code must not be empty"),
    test(validZipcode, "Must be a valid US zip code"),
  ],
});

export default () => {
  const api = useWebClientApi();
  const role = useRoleForBorrower();
  const coborrowerNavigate = useRedirectToPage("/ethnicity");
  if (role === "COBORROWER") {
    coborrowerNavigate();
  }
  const { formData, updateField } = useFormLoad<HomePlacementFormData>(
    {},
    () => api.getHome(),
    (data) => fromHomePlacement(data as DtoHome),
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: HomePlacementFormData) =>
      api.updateHome(toUpdateHomePlacementReq(data)),
    );
  useResponseRedirect("/land", response);

  const formErrors = validate<HomePlacementFormData>(
    formData,
    validationMapper,
  );

  const homeValue = useMemo(
    () => toHomePlacementFormData(formData ?? {}),
    [formData],
  );

  const visibleErrors = addressErrorsAfterSubmit(
    formErrors,
    hasAttemptedFormSubmission,
  );

  return (
    <FormLayout
      title="Where will your home be placed?"
      headerTitle="Home and Land"
      stepIndex={7}
      footerTitle="Next"
      clickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Property Address"
          subtitle="Where will the home be placed"
          headerIcon={
            <MapsLocation02Icon
              variant="bulk-rounded"
              size={16}
              className="text-[#CA7900]"
            />
          }
        >
          <FormTableBody>
            <HomeFields
              value={homeValue}
              onChange={(patch) => {
                const dlaPatch = fromHomePlacementFormData({
                  ...homeValue,
                  ...patch,
                });
                for (const [key, value] of Object.entries(dlaPatch)) {
                  updateField(key as keyof HomePlacementFormData)(value);
                }
              }}
              errors={visibleErrors}
              showPlacementSection
              {...googlePlacesFieldProps}
            />
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
